import type {
  ApiKeyResponse,
  RotateApiKeyResponse,
} from '@monorepo/shared/schemas/api-keys'
import { createFileRoute } from '@tanstack/react-router'
import { Home, Key, Plus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { ApiKeyCard } from '@/features/developers/components/ApiKeyCard'
import { apiKeysService } from '@/features/developers/services/apiKeysService'
import { useEnvironment } from '@/features/environments'
import { PageTitle } from '@/shared/components/common/PageTitle'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

export const Route = createFileRoute('/_auth/developers/api-keys')({
  component: ApiKeysPage,
})

function ApiKeysPage() {
  const [apiKey, setApiKey] = useState<ApiKeyResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const { setBreadcrumbs } = useBreadcrumbs()
  const { currentEnvironment } = useEnvironment()

  const breadcrumbsContent = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <span className='text-foreground font-normal'>Developers</span>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>API Keys</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    []
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch when environment changes
  useEffect(() => {
    let cancelled = false
    const loadApiKey = async () => {
      setLoading(true)
      try {
        const keyData = await apiKeysService.getActiveApiKey()
        if (!cancelled) setApiKey(keyData)
      } catch (error) {
        if (!cancelled) toast.error(extractErrorMessage(error))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    loadApiKey()
    return () => {
      cancelled = true
    }
  }, [currentEnvironment?.id])

  useEffect(() => {
    setBreadcrumbs(breadcrumbsContent)
  }, [setBreadcrumbs, breadcrumbsContent])

  const handleGenerateKey = async () => {
    setGenerating(true)
    try {
      const created = await apiKeysService.createApiKey({ name: 'Default' })
      setApiKey(created)
      toast.success('API key generated')
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setGenerating(false)
    }
  }

  const handleKeyRotated = (response: RotateApiKeyResponse) => {
    if (apiKey) {
      setApiKey({
        ...apiKey,
        key: response.new_key,
        key_prefix: response.key_prefix,
        updated_at: response.rotated_at,
      })
    }
  }

  if (loading) {
    return (
      <div className='space-y-6'>
        {/* Header */}
        <div className='max-w-4xl mx-auto p-6'>
          <PageTitle
            title='API Keys'
            subtitle='Manage your API keys for integrations and development'
          />
        </div>

        {/* Card Skeleton */}
        <div className='max-w-4xl mx-auto px-6'>
          <div className='animate-pulse space-y-4'>
            <div className='border rounded-lg p-6 space-y-4'>
              <div className='h-6 bg-slate-300 dark:bg-slate-600 rounded w-40'></div>
              <div className='h-10 bg-slate-300 dark:bg-slate-600 rounded'></div>
              <div className='h-8 bg-slate-300 dark:bg-slate-600 rounded w-32'></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!apiKey) {
    return (
      <div className='space-y-6'>
        {/* Header */}
        <div className='max-w-4xl mx-auto p-6'>
          <PageTitle
            title='API Keys'
            subtitle='Manage your API keys for integrations and development'
          />
        </div>

        {/* Empty State */}
        <div className='max-w-4xl mx-auto px-6'>
          <div className='text-center py-12'>
            <Key className='w-12 h-12 mx-auto mb-4 opacity-50' />
            <h3 className='text-lg font-medium mb-2'>
              No API Key for {currentEnvironment?.name || 'this environment'}
            </h3>
            <p className='text-sm text-muted-foreground mb-6'>
              Generate an API key to start integrating with the{' '}
              {currentEnvironment?.name || 'current'} environment.
            </p>
            <Button onClick={handleGenerateKey} disabled={generating}>
              <Plus className='w-4 h-4 mr-2' />
              {generating ? 'Generating...' : 'Generate API Key'}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='max-w-4xl mx-auto p-6'>
        <PageTitle
          title='API Keys'
          subtitle='Manage your API keys for integrations and development'
        />
      </div>

      {/* API Key Management */}
      <div className='max-w-4xl mx-auto px-6'>
        <ApiKeyCard apiKey={apiKey} onKeyRotated={handleKeyRotated} />
      </div>
    </div>
  )
}
